import * as React from "react";
import { ChevronDown, ChevronUp } from "react-feather";
import { MenuItem } from "../../../../store/menuSlice";
import { CollapsibleMenuItem } from "./CollapsibleMenuItem";

interface CollapsibleSectionProps {
  title: string;
  items: MenuItem[];
  expandable: boolean;
  isExpanded: boolean;
  favoriteIds: string[];
  onToggle: () => void;
  onToggleFavorite: (id: string) => void;
  customContent?: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  items,
  expandable,
  isExpanded,
  favoriteIds,
  onToggle,
  onToggleFavorite,
  customContent,
}) => {
  // Define subcategory order (GVC first, then Org, then others)
  const subCategoryOrder = ["GVC", "Org"];

  // Group items by subCategory
  const groupedItems = React.useMemo(() => {
    const groups: { subCategory: string | null; items: MenuItem[] }[] = [];
    const noSubCategory: MenuItem[] = [];
    const subCategoryMap = new Map<string, MenuItem[]>();

    items.forEach((item) => {
      if (item.subCategory) {
        if (!subCategoryMap.has(item.subCategory)) {
          subCategoryMap.set(item.subCategory, []);
        }
        subCategoryMap.get(item.subCategory)!.push(item);
      } else {
        noSubCategory.push(item);
      }
    });

    // Add items without subCategory first
    if (noSubCategory.length > 0) {
      groups.push({ subCategory: null, items: noSubCategory });
    }

    // Add grouped items in specified order
    const sortedSubCategories = Array.from(subCategoryMap.keys()).sort((a, b) => {
      const indexA = subCategoryOrder.indexOf(a);
      const indexB = subCategoryOrder.indexOf(b);

      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in the order array, it comes first
      if (indexA !== -1) return -1;
      // If only B is in the order array, it comes first
      if (indexB !== -1) return 1;
      // If neither is in the order array, sort alphabetically
      return a.localeCompare(b);
    });

    sortedSubCategories.forEach((subCategory) => {
      groups.push({ subCategory, items: subCategoryMap.get(subCategory)! });
    });

    return groups;
  }, [items]);

  return (
    <div className="collapsible-section" data-guide={`section-${title}`} data-expanded={isExpanded}>
      {title && (
        <button className="collapsible-section-header" onClick={onToggle} aria-expanded={isExpanded}>
          <h2>{title}</h2>
          {expandable ? (
            isExpanded ? (
              <ChevronUp color="var(--color-gray-800)" size={20} />
            ) : (
              <ChevronDown color="var(--color-gray-800)" size={20} />
            )
          ) : null}
        </button>
      )}

      {!expandable || isExpanded ? (
        <div className="collapsible-section-content">
          {groupedItems.map((group, groupIndex) => {
            return (
              <React.Fragment key={group.subCategory || `no-sub-${groupIndex}`}>
                {group.subCategory && (
                  <div className="menu-subcategory-header">
                    <span>{group.subCategory}</span>
                  </div>
                )}
                {group.items.map((item) => (
                  <CollapsibleMenuItem
                    key={item.id}
                    item={item}
                    isFavorited={favoriteIds.includes(item.id)}
                    onToggleFavorite={onToggleFavorite}
                  />
                ))}
              </React.Fragment>
            );
          })}
          {customContent}
        </div>
      ) : null}
    </div>
  );
};
